import {currentTasks,currentResources,retiredTrialObjects} from '../../application/current-product';
import {Checkbox} from '../../ui/Mobile';
import {Disclosure} from '../../ui/Mobile';
import {PageTitle, QuickAction, SectionHeading} from '../../ui/Mobile';
import {Input, Textarea, Picker, Button} from '../../ui/Mobile';
import { LoadingPage } from '../../ui/States';
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Link, useNavigate, useSearchParams } from "../../app/navigation";
import { useLiveQuery } from "dexie-react-hooks";
import { useService } from "../../app/context";
import { usePendingNavigation } from "../../app/usePendingNavigation";
import { ItemService } from "../../application/item-service";
import { assertWritable } from "../../infrastructure/database/database";
import {
  blankItem,
  itemPath,
  zoneSchema,
  labels,
  localDate,
  localInput,
  localDateTimeToUTC,
  type Kind,
  type Item,
  type Task,
  type Project,
} from "../../domain/items/model";
import {
  filterItems,
  homeModel,
  type Filter,
  type Candidate,
} from "../../domain/items/home";
import { AttachmentImage } from "../../ui/AttachmentImage";
import { Icon } from "../../ui/Icons";
import { contentTitle } from "../../domain/content/model";
const kindLabel = { task: "任务", event: "事件", project: "项目" };
function ItemLink({ kind, item }: { kind: Kind; item: Item }) {
  const { db } = useService();
  const project = useLiveQuery(
    () =>
      "projectId" in item && item.projectId
        ? db.projects.get(item.projectId)
        : undefined,
    [db, "projectId" in item ? item.projectId : null],
  );
  return (
    <Link className="record-row item-row" to={itemPath(kind, item.id)}>
      <div className="record-copy">
        <strong>{item.title}</strong>
        <p>
          {kindLabel[kind]} · {labels[item.state]}
          {item.archivedAt ? " · 已归档" : ""}
          {project ? ` · ${project.title}` : ""}
          {"plannedDate" in item && item.plannedDate
            ? ` · 计划 ${item.plannedDate}`
            : ""}
          {"deadline" in item && item.deadline
            ? ` · 截止 ${item.deadline.value}`
            : ""}
          {"startAt" in item
            ? ` · ${localInput(item.startAt, item.timeZone)} ${item.timeZone} · ${item.location}`
            : ""}
          {"waitingReason" in item && item.state === "waiting"
            ? ` · ${item.waitingReason} · 查看进展 ${item.reviewDate ?? "未安排"}`
            : ""}
        </p>
      </div>
    </Link>
  );
}
function ItemSnapshot({item}:{item:Item}) {return <section className="revision-content"><h3>{item.title}</h3><p>{labels[item.state]}{item.archivedAt?' · 已归档':''}{item.deletedAt?' · 回收站':''}</p><article className="capture-body">{item.body}</article>{'plannedDate'in item&&<dl><dt>计划做</dt><dd>{item.plannedDate||'未安排'}</dd><dt>安排时间</dt><dd>{item.scheduledAt||'未安排'}</dd><dt>截止</dt><dd>{item.deadline?.value||'无截止'}</dd><dt>等待原因</dt><dd>{item.waitingReason||'未填写'}</dd><dt>查看进展日期</dt><dd>{item.reviewDate||'未安排'}</dd><dt>优先级</dt><dd>{item.priority}</dd><dt>预计分钟</dt><dd>{item.durationMinutes||'未填写'}</dd></dl>}{'startAt'in item&&<p>{localInput(item.startAt,item.timeZone)}—{localInput(item.endAt,item.timeZone)} · {item.timeZone} · {item.location||'无地点'}</p>}{'outcome'in item&&<><h3>期望结果</h3><p className="capture-body">{item.outcome}</p><h3>结束结果</h3><p className="capture-body">{item.result}</p>{item.nextTaskId&&<Link to={'/tasks/'+item.nextTaskId}>查看当时的下一行动</Link>}</>}{'projectId'in item&&item.projectId&&<Link to={'/projects/'+item.projectId}>查看关联项目</Link>}<div className="attachment-grid">{item.attachmentIds.map(a=><AttachmentImage key={a} id={a}/>)}</div></section>;}
export function Items() {
  const { db } = useService();
  const [params, setParams] = useSearchParams();
  const data = useLiveQuery(
    () =>
      db.transaction("r", db.tables, async () => {const hidden=await retiredTrialObjects(db);return {
        tasks: await currentTasks(db),
        events: await db.events.toArray(),
        projects: await db.projects.toArray(),
        drafts: (await db.itemDrafts.toArray()).filter((d) => !d.deletedAt && !hidden.tasks.has(d.targetId)),
        zone: String((await db.settings.get("timeZone"))?.value ?? "UTC"),
        view: String((await db.settings.get("itemsView"))?.value ?? "today"),
      };}),
    [db],
  );
  const [error, setError] = useState(""),
    [selecting, setSelecting] = useState(false),
    [selected, setSelected] = useState<Record<string, number>>({}),
    [bulkDate, setBulkDate] = useState(""),
    [busy, setBusy] = useState(false);
  usePendingNavigation(busy);
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const filter = (params.get("view") ?? data?.view ?? "today") as Filter,
    project = params.get("project") ?? "";
  const options: Filter[] = [
    "today",
    "future",
    "unscheduled",
    "waiting",
    "completed",
    "all",
    "overdue",
  ];
  const names = {
    today: "今天",
    future: "未来",
    unscheduled: "未安排",
    waiting: "等待",
    completed: "完成",
    all: "全部",
    overdue: "逾期",
  };
  const rows = data
    ? filterItems(
        data.tasks,
        data.events,
        options.includes(filter) ? filter : "today",
        localDate(clock, data.zone),
        data.zone,
        clock,
        project,
      )
    : [];
  async function changeView(view: Filter) {
    setSelected({});
    setParams({ view, ...(project ? { project } : {}) }, { flushSync: true });
    try {
      await db.transaction("rw", db.meta, db.settings, async () => {
        await assertWritable(db);
        await db.settings.put({ key: "itemsView", value: view });
      });
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function batch(action: "plan" | "archive" | "cancel") {
    const n = Object.keys(selected).length;
    if (
      !n ||
      !confirm(
        `将${action === "plan" ? "计划" : action === "archive" ? "归档" : "取消"} ${n} 个任务？事件不受影响。`,
      )
    )
      return;
    setBusy(true);
    try {
      await new ItemService(db).batchTasks(
        selected,
        action,
        bulkDate || null,
        crypto.randomUUID(),
      );
      setSelected({});
      setSelecting(false);
      setError("已保存。");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="page items-page" id="main" data-ready={!!data}>
      <div className="page-heading"><div><p className="eyebrow">按自己的节奏安排</p><h1>全部事项</h1></div><Link className="button primary" to="/tasks/new"><Icon name="plus"/>新建任务</Link></div>
      <nav className="utility-links" aria-label="事项入口"><Link to="/inbox">收件箱</Link><Link to="/events">日程安排</Link><Link to="/events/new">新建事件</Link><Link to="/projects">空间项目</Link></nav>
      <nav className="filter-tabs" aria-label="事项筛选">
        {options.map((v) => (
          <Button
            key={v}
            aria-pressed={filter === v}
            onClick={() => void changeView(v)}
          >
            {names[v]}
          </Button>
        ))}
      </nav>
      <label>
        项目筛选
        <Picker
          value={project}
          onChange={(e) =>
            setParams({
              view: filter,
              ...(e.target.value ? { project: e.target.value } : {}),
            }, { flushSync: true })
          }
        >
          <option value="">全部项目</option>
          {data?.projects
            .filter((p) => !p.deletedAt)
            .map((p) => (
              <option value={p.id} key={p.id}>
                {p.title}
              </option>
            ))}
        </Picker>
      </label>
      {error && <p role="alert">{error}</p>}
      <p className="list-count">{data ? `${rows.length} 项` : "正在读取…"}</p>
      <Button
        disabled={busy}
        onClick={() => {
          setSelecting(!selecting);
          setSelected({});
        }}
      >
        {selecting ? "退出选择" : "选择任务"}
      </Button>
      {selecting && (
        <section className="notice">
          <h2>已选择 {Object.keys(selected).length} 个任务</h2>
          <label>
            批量计划日期
            <Input
              type="date"
              value={bulkDate}
              onChange={(e) => setBulkDate(e.target.value)}
            />
          </label>
          {(["plan", "archive", "cancel"] as const).map((a, i) => (
            <Button
              key={a}
              disabled={busy || !Object.keys(selected).length}
              onClick={() => void batch(a)}
            >
              {["计划所选任务", "归档所选任务", "取消所选任务"][i]}
            </Button>
          ))}
        </section>
      )}
      {rows.map((item) => (
        <div key={item.id}>
          {selecting && "plannedDate" in item && (
            <label>
              <Checkbox
                aria-label={`选择 ${item.title}`}
                checked={item.id in selected}
                disabled={busy}
                onChange={(e) => {
                  const next = { ...selected };
                  if (e.target.checked) next[item.id] = item.revision;
                  else delete next[item.id];
                  setSelected(next);
                }}
              />
              选择 {item.title}
            </label>
          )}
          <ItemLink kind={"startAt" in item ? "event" : "task"} item={item} />
        </div>
      ))}
      {data && !rows.length && <p>当前筛选没有事项。</p>}
      {!!data?.drafts.length && (
        <section>
          <h2>事项编辑草稿</h2>
          {data.drafts.map((d) => (
            <Link
              key={d.id}
              className="record-row item-draft-row"
              to={`${itemPath(d.kind, d.targetId)}/edit?draft=${d.id}${d.sourceCaptureId ? "&capture=" + d.sourceCaptureId : ""}`}
            >
              {d.payload.title || "未命名草稿"} · {kindLabel[d.kind]}草稿
            </Link>
          ))}
        </section>
      )}
    </main>
  );
}
export function Events() {
  const { db } = useService();
  const rows = useLiveQuery(() => db.events.toArray(), [db]);
  return (
    <main className="page" id="main">
      <h1>事件安排</h1>
      <div className="detail-actions">
        <Link to="/home">首页</Link>
        <Link to="/events/new">新建事件</Link>
        <Link to="/items?view=all">全部事项</Link>
      </div>
      {rows
        ?.filter((e) => !e.deletedAt)
        .sort(
          (a, b) =>
            a.startAt.localeCompare(b.startAt) || a.id.localeCompare(b.id),
        )
        .map((e) => (
          <ItemLink key={e.id} kind="event" item={e} />
        ))}
      {rows && !rows.filter((e) => !e.deletedAt).length && (
        <p>没有事件安排。</p>
      )}
    </main>
  );
}
export function Space() {
 const {db}=useService();const data=useLiveQuery(async()=>({projects:await db.projects.toArray(),resources:await currentResources(db),decisions:await db.decisions.toArray()}),[db]);
 const categories=[['/projects','全部项目','朝一个结果慢慢前进','page','green'],['/resources','全部资料','留下值得再看的内容','page','blue'],['/decisions','全部决定','记得当时为何这样选','leaf','peach']] as const;
 const recent=data?[...data.projects.map(p=>({id:p.id,title:p.title,path:'/projects/'+p.id,updatedAt:p.updatedAt,deletedAt:p.deletedAt,archivedAt:p.archivedAt,kind:'项目'})),...data.resources.map(p=>({id:p.id,title:contentTitle(p),path:'/resources/'+p.id,updatedAt:p.updatedAt,deletedAt:p.deletedAt,archivedAt:p.archivedAt,kind:'资料'})),...data.decisions.map(p=>({id:p.id,title:contentTitle(p),path:'/decisions/'+p.id,updatedAt:p.updatedAt,deletedAt:p.deletedAt,archivedAt:p.archivedAt,kind:'决定'}))].filter(x=>!x.deletedAt).sort((a,b)=>b.updatedAt.localeCompare(a.updatedAt)||a.id.localeCompare(b.id)).slice(0,6):[];
 return <main className="page space-page" id="main" data-ready={!!data}><PageTitle>空间</PageTitle><div className="space-categories">{categories.map(([to,title,_note,icon,color])=><Link className={'category-entry '+color} key={to} to={to} aria-label={title}><span className="category-icon"><Icon name={icon} size={24}/></span><div><h2>{title.replace('全部','')}</h2></div><span aria-hidden="true">↗</span></Link>)}</div><section className="recent-pages"><div className="section-heading"><h2>最近页面</h2><Link className="text-link" to="/projects/new">新建项目</Link></div>{!data?<p className="loading-state">正在读取…</p>:recent.length?recent.map(x=><Link className="record-row" key={x.id} to={x.path}><div className="record-copy"><strong>{x.title}</strong><p>{x.kind} · {x.updatedAt.slice(0,10)}{x.archivedAt?' · 已归档':''}</p></div><span className="row-chevron" aria-hidden="true">›</span></Link>):<p className="quiet-empty">还没有留下内容</p>}</section></main>;
}
export function Projects() {
  const { db } = useService();
  const data = useLiveQuery(() => db.projects.toArray(), [db]);
  return (
    <main className="page" id="main">
      <h1>全部项目</h1>
      <div className="detail-actions">
        <Link to="/space">空间</Link>
        <Link to="/projects/new">新建项目</Link>
      </div>
      {data
        ?.filter((p) => !p.deletedAt)
        .map((p) => (
          <ItemLink key={p.id} kind="project" item={p} />
        ))}
      {data && !data.filter((p) => !p.deletedAt).length && <p>没有项目。</p>}
    </main>
  );
}
export function ItemForm({ kind }: { kind: Kind }) {
  const { db } = useService(),
    { id } = useParams(),
    [params, setParams] = useSearchParams(),
    navigate = useNavigate();
  const source = params.get("capture"),
    draftParam = params.get("draft");
  const [form, setForm] = useState<Item>(),
    [expected, setExpected] = useState(0),
    [zone, setZone] = useState("UTC"),
    [projects, setProjects] = useState<Project[]>([]),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [pending, setPending] = useState(false),
    [saved, setSaved] = useState(true),
    [preview, setPreview] = useState(false);
  const loaded = useRef(false);
  const queue = useRef<Promise<boolean>>(Promise.resolve(true)),
    draftId = useRef(draftParam ?? crypto.randomUUID()),
    draftRev = useRef(0),
    copyId = useRef(crypto.randomUUID()),
    op = useRef({ signature: "", id: crypto.randomUUID() }),
    sourceRev = useRef(0),
    submitDone = useRef(false);
  usePendingNavigation((pending || busy || !saved) && !submitDone.current);
  useEffect(() => {
    if (loaded.current) return;
    let valid = true;
    void (async () => {
      try {
        const tz = String((await db.settings.get("timeZone"))?.value ?? "UTC"),
          service = new ItemService(db);
        const d = draftParam ? await db.itemDrafts.get(draftParam) : undefined;
        if (
          draftParam &&
          (!d || d.deletedAt || d.kind !== kind || (!!id && d.targetId !== id))
        )
          throw Error("草稿不存在或目标不匹配");
        const item =
          d?.payload ??
          (id && id !== "new"
            ? await service.get(kind, id)
            : blankItem(kind, tz));
        if (!item) throw Error("对象不存在");
        if (item.deletedAt) throw Error("请先从回收站恢复");
        if (!id && !d && "projectId" in item)
          item.projectId = params.get("project");
        if (source) {
          const c = await db.captures.get(source);
          if (!c || c.deletedAt) throw Error("原记录不存在");
          sourceRev.current = d?.sourceRevision ?? c.revision;
          if (!d) {
            item.title = c.body.split("\n")[0].slice(0, 200) || "图片记录";
            item.body = c.body;
            item.attachmentIds = [...c.attachmentIds];
          }
        }
        if (valid) {
          loaded.current = true;
          setZone(tz);
          setForm(item);
          setExpected(
            d?.baseRevision ?? (id && id !== "new" ? item.revision : 0),
          );
          draftRev.current = d?.revision ?? 0;
          setProjects(
            (await db.projects.toArray()).filter((p) => !p.deletedAt),
          );
        }
      } catch (e) {
        if (valid) setMessage((e as Error).message);
      }
    })();
    return () => {
      valid = false;
    };
  }, [db, id, kind, draftParam, source]);
  function change(patch: Partial<Item>) {
    if (!form) return;
    const next = { ...form, ...patch } as Item;
    setForm(next);
    setPreview(false);
    setSaved(false);
    setPending(true);
    queue.current = queue.current.then(async () => {
      try {
        const d = await new ItemService(db).draft(
          kind,
          next,
          expected,
          draftId.current,
          draftRev.current,
          source
            ? { captureId: source, revision: sourceRev.current }
            : undefined,
        );
        draftId.current = d.id;
        draftRev.current = d.revision;
        setMessage("已保存在本机 · 编辑草稿");
        return true;
      } catch (e) {
        setMessage((e as Error).message);
        return false;
      }
    });
    const last = queue.current;
    void last.then((ok) => {
      if (queue.current === last) {
        setPending(false);
        setSaved(ok);
        if (ok && draftParam !== draftId.current) {
          setParams(
            {
              draft: draftId.current,
              ...(source ? { capture: source } : {}),
              ...(params.get("project")
                ? { project: params.get("project")! }
                : {}),
            },
            { replace: true },
          );
        }
      }
    });
  }
  async function submit(copy = false) {
    if (!form || busy) return;
    if (source && !copy && !preview) {
      setPreview(true);
      return;
    }
    setBusy(true);
    try {
      if (!(await queue.current))
        throw Error("草稿保存未完成，请保留输入并重试");
      const input: Item = copy
        ? "outcome" in form
          ? {
              ...form,
              id: copyId.current,
              state: "planned",
              nextTaskId: null,
              result: "",
            }
          : { ...form, id: copyId.current }
        : form;
      const signature = JSON.stringify(input);
      if (op.current.signature !== signature)
        op.current = { signature, id: crypto.randomUUID() };
      const service = new ItemService(db);
      const item =
        source && !copy
          ? await service.organize(
              source,
              sourceRev.current,
              kind,
              form,
              op.current.id,
              draftId.current,
            )
          : await service.save(
              kind,
              input,
              copy ? 0 : expected,
              op.current.id,
              draftId.current,
            );
      submitDone.current = true;
      setSaved(true);
      navigate(itemPath(kind, item.id), { replace: true });
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (!form)
    return (
      <main className="page">
        <h1>{kindLabel[kind]}编辑</h1>
        <p role="status">{message || "正在读取…"}</p>
        <Link to="/items">全部事项</Link>
      </main>
    );
  const task = "plannedDate" in form ? form : undefined,
    event = "startAt" in form ? form : undefined,
    project = "outcome" in form ? form : undefined;
  const displayZone =
    event && zoneSchema.safeParse(event.timeZone).success
      ? event.timeZone
      : zone;
  function instant(value: string) {
    return localDateTimeToUTC(value, event?.timeZone ?? zone);
  }
  return (
    <main className="page item-form" id="main">
      <h1>
        {source ? "整理为" : expected ? "编辑" : "新建"}
        {kindLabel[kind]}
      </h1>
      <Link to="/items">返回全部事项</Link>
      <label>
        标题
        <Input
          maxLength={200}
          value={form.title}
          disabled={busy}
          onChange={(e) => change({ title: e.target.value })}
        />
      </label>
      <label>
        备注
        <Textarea
          value={form.body}
          disabled={busy}
          onChange={(e) => change({ body: e.target.value })}
        />
      </label>
      {!project && (
        <label>
          所属项目
          <Picker
            value={"projectId" in form ? (form.projectId ?? "") : ""}
            onChange={(e) => change({ projectId: e.target.value || null })}
          >
            <option value="">无项目</option>
            {projects.map((p) => (
              <option value={p.id} key={p.id}>
                {p.title}
              </option>
            ))}
          </Picker>
        </label>
      )}
      {task && (
        <>
          <label>
            计划做
            <Input
              type="date"
              value={task.plannedDate ?? ""}
              onChange={(e) => change({ plannedDate: e.target.value || null })}
            />
          </label>
          <label>
            安排时间
            <Input
              type="datetime-local"
              value={task.scheduledAt ? localInput(task.scheduledAt, zone) : ""}
              onChange={(e) => {
                try {
                  change({
                    scheduledAt: e.target.value
                      ? instant(e.target.value)
                      : null,
                  });
                } catch (err) {
                  setMessage((err as Error).message);
                }
              }}
            />
          </label>
          <label>
            截止类型
            <Picker
              value={task.deadline?.kind ?? ""}
              onChange={(e) =>
                change({
                  deadline:
                    e.target.value === "date"
                      ? { kind: "date", value: localDate(new Date(), zone) }
                      : e.target.value === "instant"
                        ? { kind: "instant", value: new Date().toISOString() }
                        : null,
                })
              }
            >
              <option value="">无截止</option>
              <option value="date">日期型</option>
              <option value="instant">具体时间</option>
            </Picker>
          </label>
          {task.deadline && (
            <label>
              最晚完成
              <Input
                type={task.deadline.kind === "date" ? "date" : "datetime-local"}
                value={
                  task.deadline.kind === "date"
                    ? task.deadline.value
                    : localInput(task.deadline.value, zone)
                }
                onChange={(e) => {
                  try {
                    change({
                      deadline: e.target.value
                        ? {
                            kind: task.deadline!.kind,
                            value:
                              task.deadline!.kind === "date"
                                ? e.target.value
                                : instant(e.target.value),
                          }
                        : null,
                    });
                  } catch (err) {
                    setMessage((err as Error).message);
                  }
                }}
              />
            </label>
          )}
          <label>
            状态
            <Picker
              value={task.state}
              onChange={(e) =>
                change({ state: e.target.value as Task["state"] })
              }
            >
              {["todo", "doing", "waiting", "completed", "cancelled"].map(
                (s) => (
                  <option key={s} value={s}>
                    {labels[s as Task["state"]]}
                  </option>
                ),
              )}
            </Picker>
          </label>
          <Disclosure className="item-extra"><summary>等待、优先级与时长</summary>
          <label>
            等待原因
            <Input
              value={task.waitingReason}
              onChange={(e) => change({ waitingReason: e.target.value })}
            />
          </label>
          <label>
            查看进展日期
            <Input
              type="date"
              value={task.reviewDate ?? ""}
              onChange={(e) => change({ reviewDate: e.target.value || null })}
            />
          </label>
          <label>
            优先级
            <Picker
              value={task.priority}
              onChange={(e) => change({ priority: Number(e.target.value) })}
            >
              {[0, 1, 2, 3].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Picker>
          </label>
          <label>
            预计分钟（可选）
            <Input
              type="number"
              min="1"
              max="1440"
              value={task.durationMinutes ?? ""}
              onChange={(e) =>
                change({
                  durationMinutes: e.target.value
                    ? Number(e.target.value)
                    : null,
                })
              }
            />
          </label>
          </Disclosure>
        </>
      )}
      {event && (
        <>
          <label>
            事件时区
            <Input
              value={event.timeZone}
              onChange={(e) => change({ timeZone: e.target.value })}
            />
          </label>
          <label>
            开始时间
            <Input
              type="datetime-local"
              value={localInput(event.startAt, displayZone)}
              onChange={(e) => {
                try {
                  change({ startAt: instant(e.target.value) });
                } catch (err) {
                  setMessage((err as Error).message);
                }
              }}
            />
          </label>
          <label>
            结束时间
            <Input
              type="datetime-local"
              value={localInput(event.endAt, displayZone)}
              onChange={(e) => {
                try {
                  change({ endAt: instant(e.target.value) });
                } catch (err) {
                  setMessage((err as Error).message);
                }
              }}
            />
          </label>
          <label>
            地点
            <Input
              value={event.location}
              onChange={(e) => change({ location: e.target.value })}
            />
          </label>
        </>
      )}
      {project && (
        <>
          <label>
            期望结果
            <Textarea
              value={project.outcome}
              onChange={(e) => change({ outcome: e.target.value })}
            />
          </label>
          <label>
            项目状态
            <Picker
              value={project.state}
              onChange={(e) =>
                change({ state: e.target.value as Project["state"] })
              }
            >
              {["planned", "active", "paused"].map((v) => (
                <option key={v} value={v}>
                  {labels[v as Project["state"]]}
                </option>
              ))}
              {["completed", "cancelled"].includes(project.state) && (
                <option value={project.state}>{labels[project.state]}</option>
              )}
            </Picker>
          </label>
        </>
      )}
      {!!form.attachmentIds.length && (
        <div className="attachment-grid">
          {form.attachmentIds.map((a) => (
            <AttachmentImage key={a} id={a} />
          ))}
        </div>
      )}
      <p role="status">{message}</p>
      {message.includes("新版本") && (
        <Button disabled={busy || pending} onClick={() => void submit(true)}>
          保留原对象，另存为新{kindLabel[kind]}
        </Button>
      )}
      {source && preview && (
        <section className="notice">
          <h2>整理预览</h2>
          <p>
            新建{kindLabel[kind]}：{form.title}
            。事务成功后原记录标为已整理；原文与图片保留。
          </p>
        </section>
      )}
      <Button
        className="button primary"
        disabled={busy || pending || !form.title.trim()}
        onClick={() => void submit()}
      >
        {busy
          ? "正在保存…"
          : source
            ? preview
              ? "确认整理"
              : "预览整理"
            : "保存" + kindLabel[kind]}
      </Button>
      {draftRev.current > 0 && (
        <>
          <Link to="/items">保留草稿并返回</Link>
          <Button
            disabled={busy || pending}
            onClick={() => {
              if (
                confirm(
                  "将这份编辑草稿移入回收站？已发布对象和来源原记录保留。",
                )
              ) {
                setBusy(true);
                void new ItemService(db)
                  .draftLifecycle(
                    draftId.current,
                    draftRev.current,
                    "trash",
                    crypto.randomUUID(),
                  )
                  .then(() => {
                    submitDone.current = true;
                    setSaved(true);
                    navigate("/items");
                  })
                  .catch((e) => setMessage((e as Error).message))
                  .finally(() => setBusy(false));
              }
            }}
          >
            放弃这份草稿
          </Button>
        </>
      )}
    </main>
  );
}
export function ItemDetail({ kind }: { kind: Kind }) {
  const { id = "" } = useParams(),
    { db } = useService();
  const service = new ItemService(db),
    navigate = useNavigate();
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [ending, setEnding] = useState(false),
    [result, setResult] = useState(""),
    [endState, setEndState] = useState<"completed" | "cancelled">("completed"),
    [choices, setChoices] = useState<
      Record<string, "keep" | "cancel" | "detach">
    >({}),
    [chosenVersions, setChosenVersions] = useState<Record<string, number>>({}),
    [snooze, setSnooze] = useState("");
  usePendingNavigation(busy);
  const data = useLiveQuery(
    async () => ({
      item: await new ItemService(db).get(kind, id),
      directive: await db.homeDirectives.get(id),
      tasks: (await currentTasks(db)).filter(t=>t.projectId===id),
      events: await db.events.where("projectId").equals(id).toArray(),
      project:
        kind === "task" || kind === "event"
          ? await new ItemService(db)
              .get(kind, id)
              .then((x) =>
                x && "projectId" in x && x.projectId
                  ? db.projects.get(x.projectId)
                  : undefined,
              )
          : undefined,
      history: await db.revisions
        .where("[entityType+entityId]")
        .equals([kind, id])
        .toArray(),
      zone: String((await db.settings.get("timeZone"))?.value ?? "UTC"),
    }),
    [db, kind, id],
  );
  async function action(fn: () => Promise<unknown>) {
    setBusy(true);
    setMessage("");
    try {
      await fn();
      setMessage("已保存。");
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (!data) return <LoadingPage />;
  const item = data.item;
  if (!item)
    return (
      <main className="page">
        <h1>对象不存在</h1>
        <p>未按名称重建对象。</p>
        <Link to="/items">全部事项</Link>
      </main>
    );
  const project = kind === "project" ? (item as Project) : undefined,
    actions = [...data.tasks, ...data.events].filter(
      (x) =>
        !x.deletedAt &&
        !["completed", "cancelled", "occurred"].includes(x.state),
    );
  const states =
    kind === "task"
      ? ["todo", "doing", "waiting", "completed", "cancelled"]
      : kind === "event"
        ? ["scheduled", "occurred", "cancelled"]
        : ["planned", "active", "paused"];
  return (
    <main className={"page item-detail "+kind+"-detail"} id="main">
      <Link className="back-link" to={kind === "project" ? "/projects" : "/items"}>
        返回{kind === "project" ? "全部项目" : "全部事项"}
      </Link>
      <h1>{item.title}</h1>
      <p>
        {kindLabel[kind]} · {labels[item.state]}
        {item.deletedAt ? " · 回收站" : ""}
        {item.archivedAt ? " · 已归档" : ""}
      </p>
      <div className="detail-actions">

      {kind !== "project" && !item.deletedAt && (
          <Button
            className="button primary"
            disabled={busy}
            onClick={() =>
              void action(() =>
                service.state(
                  kind,
                  id,
                  item.revision,
                  {
                    state:
                      kind === "task"
                        ? item.state === "completed"
                          ? "todo"
                          : "completed"
                        : item.state === "occurred"
                          ? "scheduled"
                          : "occurred",
                  },
                  crypto.randomUUID(),
                ),
              )
            }
          >
            {kind === "task"
              ? item.state === "completed"
                ? "撤销完成"
                : "完成任务"
              : item.state === "occurred"
                ? "撤销已发生"
                : "标为已发生"}
          </Button>
        )}
        <Link to={`${itemPath(kind, id)}/edit`}>编辑{kindLabel[kind]}</Link>
      </div>
      {"plannedDate" in item && (item.plannedDate || item.deadline || item.waitingReason || item.reviewDate) && <dl>
        {item.plannedDate && <><dt>计划做</dt><dd>{item.plannedDate}</dd></>}
        {item.deadline && <><dt>最晚完成</dt><dd>{item.deadline.value}{item.deadline.kind === 'date' ? ' · 当地日末' : ''}</dd></>}
        {item.waitingReason && <><dt>等待</dt><dd>{item.waitingReason}</dd></>}
        {item.reviewDate && <><dt>查看日</dt><dd>{item.reviewDate}</dd></>}
      </dl>}
      {"startAt" in item && (
        <p>
          {localInput(item.startAt, item.timeZone)} —{" "}
          {localInput(item.endAt, item.timeZone)} · {item.timeZone} ·{" "}
          {item.location || "未填地点"}
        </p>
      )}
      {"projectId" in item && item.projectId && (
        <p>
          项目：
          {data.project ? (
            <Link to={itemPath("project", data.project.id)}>
              {data.project.title} · {labels[data.project.state]}
            </Link>
          ) : (
            "项目缺失"
          )}
        </p>
      )}
      <article className="capture-body">{item.body}</article>
      <div className="attachment-grid">
        {item.attachmentIds.map((a) => (
          <AttachmentImage key={a} id={a} />
        ))}
      </div>

      {kind !== "project" && !item.deletedAt && (
        <section>
          <Disclosure className="recommendation-settings"><summary>推荐控制</summary>
          <label>
            推荐同层次序
            <Input
              type="number"
              min="0"
              value={data.directive?.order ?? 0}
              disabled={busy}
              onChange={(e) =>
                void action(() =>
                  service.directive(
                    id,
                    kind,
                    { order: Number(e.target.value) },
                    crypto.randomUUID(),
                  ),
                )
              }
            />
          </label>
          <Button
            disabled={busy}
            onClick={() =>
              void action(() =>
                service.directive(
                  id,
                  kind,
                  { skipDate: localDate(new Date(), data.zone) },
                  crypto.randomUUID(),
                ),
              )
            }
          >
            今天跳过
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              void action(() =>
                service.directive(
                  id,
                  kind,
                  { skipDate: null, snoozeUntil: null },
                  crypto.randomUUID(),
                ),
              )
            }
          >
            恢复推荐
          </Button>
          <label>
            稍后出现
            <Input
              type="datetime-local"
              value={snooze}
              onChange={(e) => setSnooze(e.target.value)}
            />
          </label>
          <Button
            disabled={busy || !snooze}
            onClick={() =>
              void action(() =>
                service.directive(
                  id,
                  kind,
                  { snoozeUntil: localDateTimeToUTC(snooze, data.zone) },
                  crypto.randomUUID(),
                ),
              )
            }
          >
            设置稍后
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              void action(() =>
                service.directive(
                  id,
                  kind,
                  { reminderDismissed: !data.directive?.reminderDismissed },
                  crypto.randomUUID(),
                ),
              )
            }
          >
            {data.directive?.reminderDismissed ? "恢复提醒" : "关闭此项提醒"}
          </Button>
          <p>
            固定、跳过与稍后只控制推荐，不改计划或截止。完成后的固定暂不展示，撤销完成后恢复。
          </p>
          </Disclosure>
        </section>
      )}
      {project && (
        <>
          <section className="detail-section"><h2>期望结果</h2>
          <p>{project.outcome || "尚未填写"}</p>
          </section><section className="detail-section"><h2>下一行动</h2>
          {project.nextTaskId ? (
            <p>
              <Link to={itemPath("task", project.nextTaskId)}>
                {data.tasks.find((t) => t.id === project.nextTaskId)?.title ??
                  "关联行动缺失"}
              </Link>
            </p>
          ) : (
            <p>尚未选择下一行动</p>
          )}
          <label>
            选择下一行动
            <Picker
              disabled={busy}
              value={project.nextTaskId ?? ""}
              onChange={(e) =>
                void action(() =>
                  service.state(
                    "project",
                    id,
                    project.revision,
                    { nextTaskId: e.target.value || null },
                    crypto.randomUUID(),
                  ),
                )
              }
            >
              <option value="">未选择</option>
              {data.tasks
                .filter(
                  (t) =>
                    !t.deletedAt &&
                    !["completed", "cancelled"].includes(t.state),
                )
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
            </Picker>
          </label>
          </section><section className="detail-section"><h2>关联行动</h2>
          {data.tasks
            .filter((t) => !t.deletedAt)
            .map((t) => (
              <ItemLink key={t.id} kind="task" item={t} />
            ))}
          {data.events
            .filter((t) => !t.deletedAt)
            .map((t) => (
              <ItemLink key={t.id} kind="event" item={t} />
            ))}
          <Link className="button quiet" to={`/tasks/new?project=${id}`}>添加项目任务</Link>
          </section><section className="detail-section"><h2>结束结果</h2>
          <p>{project.result || "尚未结束"}</p>
          {!item.deletedAt &&
            !["completed", "cancelled"].includes(project.state) && (
              <Button onClick={() => setEnding(true)}>结束项目</Button>
            )}
          {ending && (
            <section className="notice">
              <h2>处理剩余行动 · {actions.length} 项</h2>
              {actions.map((a) => (
                <label key={a.id}>
                  {a.title}
                  <Picker
                    value={choices[a.id] ?? ""}
                    onChange={(e) => {
                      setChoices({
                        ...choices,
                        [a.id]: e.target.value as "keep" | "cancel" | "detach",
                      });
                      setChosenVersions({
                        ...chosenVersions,
                        [a.id]: a.revision,
                      });
                    }}
                  >
                    <option value="">请选择</option>
                    <option value="keep">保留关联与原状态</option>
                    <option value="cancel">明确取消此项</option>
                    <option value="detach">移出项目，状态保留</option>
                  </Picker>
                </label>
              ))}
              <label>
                结束状态
                <Picker
                  value={endState}
                  onChange={(e) =>
                    setEndState(e.target.value as typeof endState)
                  }
                >
                  <option value="completed">完成</option>
                  <option value="cancelled">取消</option>
                </Picker>
              </label>
              <label>
                结果
                <Textarea
                  value={result}
                  onChange={(e) => setResult(e.target.value)}
                />
              </label>
              <Button
                disabled={
                  busy || !result.trim() || actions.some((a) => !choices[a.id])
                }
                onClick={() =>
                  void action(async () => {
                    await service.endProject(
                      id,
                      item.revision,
                      endState,
                      result,
                      choices,
                      crypto.randomUUID(),
                      chosenVersions,
                    );
                    setEnding(false);
                  })
                }
              >
                确认结束项目
              </Button>
              <Button onClick={() => setEnding(false)}>返回处理</Button>
            </section>
          )}
          </section>
        </>
      )}
      <Disclosure className="item-management secondary-panel"><summary>更多操作</summary>
      {project&&<p>项目暂停时普通行动停止推荐，事件与硬截止仍提醒。不会自动完成关联行动。</p>}
      {!item.deletedAt && (
        <label className="item-state-control">
          状态
          <Picker
            disabled={busy}
            value={item.state}
            onChange={(e) =>
              void action(() =>
                service.state(
                  kind,
                  id,
                  item.revision,
                  { state: e.target.value as Item["state"] },
                  crypto.randomUUID(),
                ),
              )
            }
          >
            {states.map((s) => (
              <option value={s} key={s}>
                {labels[s as keyof typeof labels]}
              </option>
            ))}
            {project && ["completed", "cancelled"].includes(project.state) && (
              <option value={project.state}>{labels[project.state]}</option>
            )}
          </Picker>
        </label>
      )}
        <div className="detail-actions management-actions">        <Button
          disabled={busy || !!item.deletedAt}
          onClick={() =>
            void action(() =>
              service.directive(
                id,
                kind,
                { pinned: !data.directive?.pinned },
                crypto.randomUUID(),
              ),
            )
          }
        >
          {data.directive?.pinned ? "解除固定" : "固定"}
        </Button>
        <Button
          disabled={busy || !!item.deletedAt}
          onClick={() =>
            void action(() =>
              service.state(
                kind,
                id,
                item.revision,
                {
                  archivedAt: item.archivedAt ? null : new Date().toISOString(),
                },
                crypto.randomUUID(),
              ),
            )
          }
        >
          {item.archivedAt ? "撤销归档" : "归档"}
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            if (
              kind === "project" &&
              !item.deletedAt &&
              !confirm(
                `移入回收站会暂停项目普通推荐；${actions.length} 项未结束关联行动与事件仍保留，硬截止仍提醒。继续？`,
              )
            )
              return;
            void action(() =>
              service.lifecycle(
                kind,
                id,
                item.revision,
                item.deletedAt ? "restore" : "trash",
                crypto.randomUUID(),
              ),
            );
          }}
        >
          {item.deletedAt ? "从回收站恢复" : "移入回收站"}
        </Button>
      </div>
      </Disclosure>
      <p role="status">{message}</p>
      <Disclosure>
        <summary>修改历史 · {data.history.length} 个版本</summary>
        {data.history
          .filter((r) => "entityType" in r)
          .map((r) => (
            <div className="history-row" key={r.id}>
              版本 {r.revision} · {new Date(r.recordedAt).toLocaleString("zh-CN")}
              <ItemSnapshot item={r.snapshot as Item}/>
            </div>
          ))}
      </Disclosure>
      {item.deletedAt && (
        <Button
          disabled={busy}
          onClick={() => {
            if (confirm("永久删除此对象及其历史？关联必须先处理。"))
              void action(async () => {
                await service.lifecycle(
                  kind,
                  id,
                  item.revision,
                  "purge",
                  crypto.randomUUID(),
                );
                navigate("/trash");
              });
          }}
        >
          永久删除此对象
        </Button>
      )}
    </main>
  );
}
export function Home() {
  const { db } = useService();
  const [now, setNow] = useState(() => new Date()),
    [budget, setBudget] = useState<number>(),
    [held, setHeld] = useState<Candidate | null>(),
    [message, setMessage] = useState("");
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const data = useLiveQuery(
    () =>
      db.transaction("r", db.tables, async () => ({
        studyPlans: await db.manualStudyPlans.toArray(),
        tasks: await currentTasks(db),
        events: await db.events.toArray(),
        projects: await db.projects.toArray(),
        directives: await db.homeDirectives.toArray(),
        count: await db.captures
          .filter((c) => !c.deletedAt && !c.archivedAt && c.state === "inbox")
          .count(),
        zone: String((await db.settings.get("timeZone"))?.value ?? "UTC"),
      })),
    [db],
  );
  const model = data
    ? homeModel(
        data.tasks,
        data.events,
        data.projects,
        data.directives,
        now,
        data.zone,
        budget,
        data.studyPlans,
      )
    : undefined;
  const signature = (c: Candidate | null | undefined) =>
    c ? JSON.stringify([c.id, c.title, c.reason, c.rank, c.action]) : "";
  const baseline = useRef("");
  useEffect(() => {
    if (held === undefined && model) {
      baseline.current = model.candidates.map(signature).join("|");
      setHeld(model.candidates[0] ?? null);
    }
  }, [held, model]);
  const allSignature = model ? model.candidates.map(signature).join("|") : "";
  useEffect(() => {
    baseline.current = allSignature;
  }, [held]);
  const updated = held !== undefined && model && baseline.current !== allSignature;
  async function control(patch: {
    skipDate?: string;
    snoozeUntil?: string;
    pinned?: boolean;
  }) {
    if (!held) return;
    try {
      await new ItemService(db).directive(
        held.id,
        held.kind,
        patch,
        crypto.randomUUID(),
      );
      const directives = await db.homeDirectives.toArray();
      setHeld(
        homeModel(
          data!.tasks,
          data!.events,
          data!.projects,
          directives,
          now,
          data!.zone,
          budget,
          data!.studyPlans,
        ).candidates[0] ?? null,
      );
    } catch (e) {
      setMessage((e as Error).message);
    }
  }
  return <main className="page home-page" id="main" data-ready={!!data && held !== undefined} aria-busy={!data || held === undefined}>
    <div className="home-heading"><div><PageTitle>首页</PageTitle><p className="eyebrow">{model?.day ?? '正在读取…'}</p></div></div>
    {!!model?.reminders.length&&<section aria-label="时间提醒" className="home-reminders"><div className="section-heading"><h2>时间提醒</h2><Link className="text-link" to="/events">日程安排</Link></div>{model.reminders.map(x=><div key={x.id}><ItemLink kind={'startAt'in x?'event':'task'} item={x}/>{'deadline'in x&&x.deadline?.kind==='date'&&<p className="small-note">{x.deadline.value===model.day?'今天截止':'截止 '+x.deadline.value} · 无分钟倒计时</p>}</div>)}</section>}
    <section aria-label="下一步" className={'home-next '+(!held?'home-at-rest':'')}>{held?<><h2><Link to={itemPath(held.kind,held.id)}>{held.title}</Link></h2><p className="next-reason">{held.reason}</p><Link className="button primary" to={itemPath(held.kind,held.id)}>{held.action==='check'?'查看进展':held.kind==='event'?'查看安排':'打开行动'}</Link><Disclosure className="home-options"><summary>调整这次建议</summary><div className="next-secondary"><Button onClick={()=>{const list=model?.candidates??[],index=list.findIndex(c=>c.id===held.id);setHeld(list[(index+1)%list.length]??null);}}>换一个</Button><Link to="/items?view=all">自己选</Link></div><div className="action-group"><Button onClick={()=>void control({pinned:true})}>固定当前行动</Button><Button onClick={()=>void control({skipDate:model!.day})}>今天跳过</Button><Button onClick={()=>void control({snoozeUntil:new Date(now.getTime()+3600000).toISOString()})}>一小时后出现</Button></div></Disclosure></>:<>{model && held !== undefined && !model.reminders.length && <h2>今天没有要紧的事</h2>}<Link className="button primary" to="/capture/new?view=sheet">记一下</Link><Link className="text-link" to="/items?view=all">选择行动</Link></>}{updated&&<Button className="text-button" onClick={()=>setHeld(model?.candidates[0]??null)}>有更新，查看最新建议</Button>}</section>
    <section className="home-capture"><SectionHeading>快速记录</SectionHeading><div className="quick-actions"><QuickAction to="/diaryEntries/new" icon="pen" title="写日记" tone="peach"/><QuickAction to="/tasks/new" icon="plus" title="新事项"/><QuickAction to="/capture/new?view=sheet" icon="inbox" title="随手记" tone="blue"/></div></section>
    <nav className="home-shortcuts" aria-label="首页入口"><Link to="/items">全部事项</Link><Link className={data?.count ? 'inbox-attention' : ''} to="/inbox">收件箱{data?.count ? ' · '+data.count : ''}</Link><Link to="/events">日程安排</Link></nav>
    {!!model?.today.length&&<section><div className="section-heading"><h2 aria-label={'今天 · '+model.today.length+' 项'}>今天</h2><Link className="text-link" to="/items?view=today">查看完整今天</Link></div>{model.today.slice(0,5).map(x=><ItemLink key={x.id} kind={'startAt'in x?'event':'task'} item={x}/>)}</section>}
    {!!model?.focus.length&&<section><div className="section-heading"><h2>当前关注</h2><Link className="text-link" to="/projects">全部项目</Link></div>{model.focus.slice(0,2).map(x=><ItemLink key={x.id} kind="project" item={x}/>)}</section>}
    <Disclosure className="home-options"><summary>按时间选行动</summary><label>可用分钟（仅自行选择后筛选）<Input type="number" min="1" value={budget??''} onChange={e=>setBudget(e.target.value?Number(e.target.value):undefined)}/></label></Disclosure>
    {!!model?.overdue.length&&<Link className="text-link overdue-link" to="/items?view=overdue">查看需要重新安排的事项</Link>}<p role="status">{message}</p>
  </main>;
}
